import { motion, useTransform, useViewportScroll } from 'framer-motion';
import React from 'react';
import styled from 'styled-components';
import { device } from '../../styles/devices';
import { color, gutter, responseFont } from '../../styles/mixins';
import { p16 } from '../../styles/typography';
import { IconButton } from '../button/icon-button';
import { Container } from '../container/container';
import { LazyShow } from '../reveal/reveal';
import { SrOnly } from '../sr-only/sr-only';
import ScrollDownSvg from '../svg/scroll-down';

const Host = styled.div<{
  imageUrl?: string;
  mobileUrl: string;
  mobile2xUrl: string;
}>`
  background-image: url('${(props) => props.mobileUrl}');

  background-position: center center;
  background-size: cover;
  margin-top: -${gutter(2)};

  color: ${color('white')};
  text-align: center;

  @media only screen and (-webkit-min-device-pixel-ratio: 1.3),
    only screen and (min--moz-device-pixel-ratio: 1.3),
    only screen and (-o-min-device-pixel-ratio: 2.6/2),
    only screen and (min-device-pixel-ratio: 1.3),
    only screen and (min-resolution: 124.8dpi),
    only screen and (min-resolution: 1.3dppx) {
    background-image: url('${(props) => props.mobile2xUrl}');
  }

  @media ${device.tablet} {
    background-image: url('${(props) => props.imageUrl}');
  }
`;

const Flex = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 183px);

  @media ${device.tablet} {
    min-height: calc(100vh - 150px);
  }
`;

const Content = styled(motion.div)``;

const Title = styled.h1`
  ${responseFont(48, 64)}
  font-family: ${(props) => props.theme.font.fontMercuryDisplay};
  font-weight: 500;
  line-height: ${72 / 64};
  margin-bottom: ${gutter(2)};
`;

const Copy = styled.p`
  ${p16};
`;

const ScrollDown = styled.div`
  position: absolute;
  bottom: 56px;
`;
const Bounce = styled(motion.span)`
  display: block;
  position: relative;
`;

interface HeroProps {
  title: string;
  copy: string;
  imageUrl: string;
  mobileImageUrl: string;
  mobileImageUrl2x: string;
}

const bounceTransition = {
  y: {
    duration: 0.4,
    yoyo: Infinity,
    ease: 'easeOut',
  },
};

export const Hero = ({
  title,
  copy,
  imageUrl,
  mobileImageUrl,
  mobileImageUrl2x,
}: HeroProps) => {
  const { scrollYProgress } = useViewportScroll();

  const yValue = useTransform(scrollYProgress, [0, 1], [0, -700]);

  return (
    <Host
      imageUrl={imageUrl}
      mobileUrl={mobileImageUrl}
      mobile2xUrl={mobileImageUrl2x}
    >
      <Container>
        <Flex>
          <LazyShow>
            <Content
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              style={{ y: yValue }}
            >
              <Title>{title}</Title>
              <Copy>{copy}</Copy>
            </Content>
          </LazyShow>
          <ScrollDown>
            <IconButton
              variant="tertiary"
              onClick={() =>
                window.scrollTo({
                  top: window?.innerHeight,
                  behavior: 'smooth',
                })
              }
            >
              <Bounce
                transition={bounceTransition}
                animate={{
                  y: ['30%', '0%'],
                }}
              >
                <ScrollDownSvg />
              </Bounce>
              <SrOnly>Scroll Down</SrOnly>
            </IconButton>
          </ScrollDown>
        </Flex>
      </Container>
    </Host>
  );
};
